import { createFileRoute, Link } from "@tanstack/react-router";
import { useSuspenseQuery, queryOptions } from "@tanstack/react-query";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { supabase } from "@/integrations/supabase/client";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Phone, MessageCircle, Mail } from "lucide-react";

const settingsQuery = queryOptions({
  queryKey: ["settings"],
  queryFn: async () => {
    const { data } = await supabase.from("settings").select("*").eq("id", 1).maybeSingle();
    return data;
  },
});

type Faq = { q: string; a: string };
type Section = { title: string; items: Faq[] };

const SECTIONS: Section[] = [
  {
    title: "Vehicles & special requests",
    items: [
      {
        q: "What kind of cars do you offer?",
        a: "Creed specializes in premium daily drivers — late-model BMW, Mercedes-Benz, Audi, Lexus, Range Rover, and comparable vehicles. Browse the current lineup on the Fleet page.",
      },
      {
        q: "Can I rent for a photoshoot, music video, or event?",
        a: "Yes. We work with photographers, production teams, and event planners regularly. Mention the use case when you reach out so we can confirm coverage and pricing.",
      },
      {
        q: "Do you offer long-term or monthly rentals?",
        a: "Yes — monthly rentals are one of our most popular options for relocators, contractors, and snowbirds. Contact us for custom pricing.",
      },
      {
        q: "Can I reserve a specific car in advance?",
        a: "Yes. Reservations lock in the exact vehicle, dates, and rate. The earlier you book, the better the availability — especially in peak season (November–April).",
      },
    ],
  },
  {
    title: "Renting & eligibility",
    items: [
      {
        q: "How old do I need to be to rent?",
        a: "Drivers must be 25 or older with a valid driver's license. Drivers aged 25–29 may be subject to a young-driver surcharge depending on the vehicle.",
      },
      {
        q: "Can I use an international or out-of-state driver's license?",
        a: "Yes. Valid U.S. licenses from any state and valid international driver's licenses are accepted.",
      },
      {
        q: "Do I need a credit card?",
        a: "Yes. A major credit card in the renter's name is required to hold the refundable security deposit and complete the rental agreement.",
      },
      {
        q: "Can someone else drive the car?",
        a: "Additional drivers must be present at pickup, meet the same age and license requirements, and be added to the rental agreement. A small additional-driver fee may apply.",
      },
    ],
  },
  {
    title: "Pricing & deposits",
    items: [
      {
        q: "Is a security deposit required?",
        a: "Yes. A refundable security deposit is held (not charged) on your credit card at pickup and released after the vehicle is returned in the same condition. The amount typically starts at $300 and varies by vehicle.",
      },
      {
        q: "What's included in the daily rate?",
        a: "Your daily rate includes insurance, a daily mileage allowance, and 24/7 roadside assistance. Delivery, fuel, tolls, and any add-ons are billed separately.",
      },
      {
        q: "Is there a mileage limit?",
        a: "Most rentals include a generous daily mileage allowance. Additional miles can be purchased upfront at a discounted rate, or billed per mile at return. Unlimited mileage is available on long-term rentals.",
      },
      {
        q: "Do you offer weekly or monthly discounts?",
        a: "Yes. Weekly and monthly rates are discounted significantly versus the daily rate. Contact us for a custom long-term quote.",
      },
      {
        q: "What is your cancellation policy?",
        a: "Cancellations made more than 48 hours before pickup receive a full refund. Cancellations within 48 hours of pickup forfeit the first day's rental rate. No-shows are charged the full rental amount.",
      },
    ],
  },
  {
    title: "Delivery & pickup",
    items: [
      {
        q: "Do you deliver to Miami International (MIA) and Fort Lauderdale (FLL)?",
        a: "Yes. We deliver directly to MIA and FLL airports as well as hotels, residences, and offices throughout Miami-Dade and Broward.",
      },
      {
        q: "Is there a delivery fee?",
        a: "Delivery within central Miami is often complimentary or low-cost depending on the rental length. Airport, Fort Lauderdale, and farther deliveries are quoted based on distance — text us your address for an instant quote.",
      },
      {
        q: "What time can I pick up or return the car?",
        a: "Pickup and drop-off happen during our standard business hours, listed on the Contact page. After-hours service can be arranged in advance for a small fee — just let us know your flight or schedule.",
      },
      {
        q: "Can I extend my rental?",
        a: "Absolutely. Just call or text us at least 24 hours before your return time and we'll extend the agreement and charge the additional days at your existing rate.",
      },
    ],
  },
  {
    title: "Insurance & damage",
    items: [
      {
        q: "Is insurance included?",
        a: "Yes. All rentals include the state-required liability coverage. Renters may also use their personal auto insurance or credit card coverage as primary if they prefer.",
      },
      {
        q: "What happens if the car is damaged?",
        a: "Any new damage is documented at return. Minor damage may be deducted from the security deposit; larger claims are processed through the included insurance. We walk every customer through the inspection at pickup and return so there are no surprises.",
      },
      {
        q: "What about tolls and traffic tickets?",
        a: "Tolls are billed back to the renter at cost plus a small administrative fee. Any tickets or citations received during the rental are the renter's responsibility.",
      },
      {
        q: "Do I need to return the car with a full tank?",
        a: "Yes. Cars are delivered full and should be returned full. If returned with less fuel, the difference is billed at the local pump rate plus a refueling fee.",
      },
      {
        q: "Is smoking allowed in the vehicles?",
        a: "All vehicles are non-smoking. A cleaning fee will apply if smoke odor or residue is detected at return.",
      },
    ],
  },
];

const faqJsonLd = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: SECTIONS.flatMap((s) =>
    s.items.map((i) => ({
      "@type": "Question",
      name: i.q,
      acceptedAnswer: { "@type": "Answer", text: i.a },
    })),
  ),
};

export const Route = createFileRoute("/faq")({
  head: () => ({
    meta: [
      { title: "Luxury Car Rental Miami FAQ | Requirements & Pricing | Creed" },
      {
        name: "description",
        content:
          "Miami luxury car rental FAQ: age, deposits, MIA & FLL delivery, insurance, mileage & long-term rates. Get answers before booking with Creed.",
      },
      { property: "og:title", content: "Luxury Car Rental Miami FAQ | Requirements & Pricing | Creed" },
      {
        property: "og:description",
        content:
          "Everything you need to know before renting with Creed Car Rental: requirements, deposits, delivery, insurance, and more.",
      },
      { property: "og:url", content: "https://creedcarental.com/faq" },
    ],
    links: [{ rel: "canonical", href: "https://creedcarental.com/faq" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(faqJsonLd),
      },
    ],
  }),
  loader: ({ context }) => context.queryClient.ensureQueryData(settingsQuery),
  component: FaqPage,
  errorComponent: () => <div className="p-10 text-center">Failed to load.</div>,
  notFoundComponent: () => <div className="p-10 text-center">Page not found.</div>,
});

function FaqPage() {
  const { data: settings } = useSuspenseQuery(settingsQuery);
  const phone = settings?.contact_phone ?? "+13055209765";
  const email = settings?.contact_email ?? "creedcarental@gmail.com";
  const waNumber = (phone || "").replace(/\D/g, "");

  return (
    <div className="min-h-screen bg-cream">
      <SiteHeader />

      {/* Hero header */}
      <section className="relative isolate overflow-hidden border-b border-border/60 bg-espresso py-20 md:py-24">
        <div
          className="pointer-events-none absolute inset-0 opacity-60"
          style={{
            background:
              "radial-gradient(60% 80% at 85% 20%, oklch(0.72 0.15 65 / 0.35), transparent 60%), radial-gradient(50% 70% at 10% 90%, oklch(0.72 0.15 65 / 0.18), transparent 60%)",
          }}
          aria-hidden
        />
        <svg
          className="pointer-events-none absolute -right-24 -top-24 h-[520px] w-[520px] text-accent/20"
          viewBox="0 0 600 600"
          fill="none"
          aria-hidden
        >
          <circle cx="300" cy="300" r="220" stroke="currentColor" strokeWidth="1" />
          <circle cx="300" cy="300" r="160" stroke="currentColor" strokeWidth="1" />
          <circle cx="300" cy="300" r="100" stroke="currentColor" strokeWidth="1" />
        </svg>
        <div className="relative mx-auto max-w-4xl px-6 text-center">
          <div className="text-xs uppercase tracking-[0.3em] text-accent">Frequently Asked</div>
          <h1 className="mt-4 font-display text-5xl text-cream md:text-6xl">
            Everything you need to know
          </h1>
          <p className="mx-auto mt-4 max-w-2xl text-cream/70">
            From age requirements and deposits to airport delivery and long-term rentals — here are the answers we get most often. Still have questions? We're one call away.
          </p>
        </div>
      </section>

      {/* FAQ sections */}
      <section className="mx-auto max-w-4xl px-6 py-14">
        <div className="space-y-10">
          {SECTIONS.map((section) => (
            <div key={section.title} className="rounded-lg border border-border bg-card p-6 md:p-8">
              <h2 className="font-display text-2xl text-espresso">{section.title}</h2>
              <Accordion type="single" collapsible className="mt-3">
                {section.items.map((item, i) => (
                  <AccordionItem key={i} value={`${section.title}-${i}`}>
                    <AccordionTrigger className="text-left">{item.q}</AccordionTrigger>
                    <AccordionContent className="text-muted-foreground">
                      {item.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="mt-12 rounded-lg border border-border bg-primary p-8 text-center text-primary-foreground">
          <h2 className="font-display text-2xl md:text-3xl">Still have questions?</h2>
          <p className="mx-auto mt-2 max-w-xl text-sm text-primary-foreground/80">
            Talk to a Creed concierge directly. We reply within the hour, 7 days a week.
          </p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
            <Button asChild variant="secondary" className="bg-cream text-espresso hover:bg-cream/90">
              <a href={`tel:${phone}`}>
                <Phone className="mr-2 h-4 w-4" /> Call {phone}
              </a>
            </Button>
            <Button asChild variant="secondary" className="bg-cream text-espresso hover:bg-cream/90">
              <a href={`https://wa.me/${waNumber}`} target="_blank" rel="noopener noreferrer">
                <MessageCircle className="mr-2 h-4 w-4" /> WhatsApp
              </a>
            </Button>
            <Button asChild variant="secondary" className="bg-cream text-espresso hover:bg-cream/90">
              <a href={`mailto:${email}`}>
                <Mail className="mr-2 h-4 w-4" /> Email
              </a>
            </Button>
          </div>
          <div className="mt-4 text-sm">
            <Link to="/contact" className="underline underline-offset-4">
              Or send us a message →
            </Link>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
